import React from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import moment from 'moment';
import { Column } from 'components/shared/Layout';
import { VoucherStatus, VoucherLanguages, esVoucherTypes, erVoucherTypes } from 'constants/vouchers';
import {
  FormContainer,
  StyledLabel,
  StyledPriceWrapper,
  StyledPriceLabel,
  StyledCurrencyInput,
  StyledTextInput,
  StyledSelect,
  StyledDateInput,
  DatesWrapper,
  DateItem,
  DateSeparator,
  StyledDatesRow,
  StyledCreatedInfoRow,
  StyledRow,
  StyledLinkableTextInput,
  OriginalDateIcon,
  TooltipContainer
} from './VoucherDetails.styles';
import Information from 'assets/rebrand/icons/information.svg';
import ReactTooltip from 'react-tooltip';
import { useVoucherEditSettings } from './useVoucherEditSettings';

const DATE_DISPLAY_FORMAT = 'Do MMM YYYY';
const DATE_TIME_DISPLAY_FORMAT = 'Do MMM YYYY [at] HH:mm';
const DATE_FORMAT = 'YYYY-MM-DD';

const VoucherDetailsTable = ({
  voucherAlias,
  voucherOriginalValue,
  voucherCurrentBalance,
  voucherCurrency,
  voucherDescription,
  voucherStatus,
  voucherType,
  salesPeriodStart,
  salesPeriodEnd,
  travelPeriodStart,
  travelPeriodEnd,
  originalSalesPeriodEnd,
  originalTravelPeriodEnd,
  createdBy,
  createdAt,
  updatedBy,
  updatedAt,
  activatedAt,
  inactivatedAt,
  language,
  associatedPNR,
  associatedVoucher,
  replacedByVoucher,
  register
}) => {
  const editSettings = useVoucherEditSettings();

  const isEsOrErVoucher = esVoucherTypes.includes(voucherType) || erVoucherTypes.includes(voucherType);
  return (
    <FormContainer>
      <StyledRow columns={12}>
        <Column span={4}>
          <StyledLabel htmlFor="voucherAlias">
            <FormattedMessage id="vouchers.voucherAlias" />
          </StyledLabel>
          <StyledTextInput name="voucherAlias" type="text" value={voucherAlias} readOnly data-cy="voucher-alias" />
        </Column>
        <Column span={4}>
          <StyledPriceWrapper>
            <StyledPriceLabel htmlFor="voucherOriginalValue">
              <FormattedMessage id="vouchers.originalBalance" />
            </StyledPriceLabel>
            <StyledCurrencyInput
              currency={voucherCurrency}
              isControlled
              name="voucherOriginalValue"
              value={parseFloat(voucherOriginalValue).toFixed(2)}
              readOnly
              data-cy="voucher-original-balance"
            />
          </StyledPriceWrapper>
        </Column>
        <Column span={4}>
          <StyledPriceWrapper>
            <StyledPriceLabel htmlFor="voucherCurrentBalance">
              <FormattedMessage id="vouchers.currentBalance" />
            </StyledPriceLabel>
            <StyledCurrencyInput
              currency={voucherCurrency}
              isControlled
              name="voucherCurrentBalance"
              value={parseFloat(voucherCurrentBalance).toFixed(2)}
              readOnly
              data-cy="voucher-current-balance"
            />
          </StyledPriceWrapper>
        </Column>
      </StyledRow>
      <StyledRow columns={12}>
        <Column span={4}>
          <StyledLabel htmlFor="voucherStatus">
            <FormattedMessage id="vouchers.voucherStatus" />
          </StyledLabel>
          {editSettings.fields.status && (
            <StyledSelect {...register('voucherStatus', { required: true })} id="voucherStatus" data-cy="voucher-status">
              <option key={VoucherStatus.ACTIVE} value={VoucherStatus.ACTIVE}>
                {VoucherStatus.ACTIVE}
              </option>
              <option key={VoucherStatus.INACTIVE} value={VoucherStatus.INACTIVE}>
                {VoucherStatus.INACTIVE}
              </option>
            </StyledSelect>
          )}
          {!editSettings.fields.status && (
            <StyledTextInput name="voucherStatus" type="text" value={VoucherStatus[voucherStatus]} readOnly data-cy="voucher-status" />
          )}
        </Column>
        <Column span={4}>
          <StyledLabel htmlFor="voucherDescription">
            <FormattedMessage id="vouchers.voucherType" />
          </StyledLabel>
          <StyledTextInput name="voucherDescription" type="text" value={voucherDescription} readOnly data-cy="voucher-description" />
        </Column>
        <Column span={4}>
          <StyledLabel htmlFor="associatedPNR">
            <FormattedMessage id="createVoucher.fields.associatedPNR" />
          </StyledLabel>
          <StyledLinkableTextInput
            to={`/manage/bookings/${associatedPNR}/journeys`}
            name="associatedPNR"
            type="text"
            value={associatedPNR}
            readOnly
            data-cy="voucher-customer-associated-pnr"
          />
        </Column>
      </StyledRow>
      <StyledDatesRow columns={12}>
        <Column span={4}>
          <DatesWrapper>
            <DateItem>
              <StyledLabel htmlFor="salesPeriodStart">
                <FormattedMessage id="vouchers.salesPeriodStart" />
              </StyledLabel>
              <StyledDateInput
                type="text"
                name="salesPeriodStart"
                value={moment(salesPeriodStart).format(DATE_DISPLAY_FORMAT)}
                readOnly
                data-cy="voucher-sales-start"
              />
            </DateItem>
            <DateSeparator>-</DateSeparator>
            <DateItem>
              <StyledLabel htmlFor="salesPeriodEnd">
                <FormattedMessage id="vouchers.salesPeriodEnd" />
                {originalSalesPeriodEnd && (
                  <>
                    <OriginalDateIcon src={Information} data-for="original_sales_date_tooltip" data-tip />
                    <ReactTooltip
                      className="tooltip"
                      id="original_sales_date_tooltip"
                      data-cy="voucher-detail-original-sales-tooltip"
                      place="bottom"
                    >
                      <TooltipContainer>
                        <span>Voucher extended</span>
                        {`Original sales end date: ${moment(originalSalesPeriodEnd).format(DATE_DISPLAY_FORMAT)}`}
                      </TooltipContainer>
                    </ReactTooltip>
                  </>
                )}
              </StyledLabel>
              <StyledDateInput
                type="date"
                min={moment().format(DATE_FORMAT)}
                max={
                  isEsOrErVoucher
                    ? moment(salesPeriodEnd).add(3, 'month').format(DATE_FORMAT)
                    : moment(salesPeriodEnd).add(1, 'year').format(DATE_FORMAT)
                }
                defaultValue={moment(salesPeriodEnd).format(DATE_FORMAT)}
                {...register('salesPeriodEnd', { required: true })}
                data-cy="voucher-sales-end-editable"
                hide={!editSettings.fields.salesDate}
              />
              <StyledDateInput
                type="text"
                defaultValue={moment(salesPeriodEnd).format(DATE_DISPLAY_FORMAT)}
                readOnly
                data-cy="voucher-sales-end-readonly"
                hide={editSettings.fields.salesDate}
              />
            </DateItem>
          </DatesWrapper>
        </Column>
        <Column span={4}>
          <DatesWrapper>
            <DateItem>
              <StyledLabel htmlFor="travelPeriodStart">
                <FormattedMessage id="vouchers.travelPeriodStart" />
              </StyledLabel>
              <StyledDateInput
                type="text"
                name="travelPeriodStart"
                value={moment(travelPeriodStart).format(DATE_DISPLAY_FORMAT)}
                readOnly
                data-cy="voucher-travel-start"
              />
            </DateItem>
            <DateSeparator>-</DateSeparator>
            <DateItem>
              <StyledLabel htmlFor="travelPeriodEnd">
                <FormattedMessage id="vouchers.travelPeriodEnd" />
                {originalTravelPeriodEnd && (
                  <>
                    <OriginalDateIcon src={Information} data-for="original_travel_date_tooltip" data-tip />
                    <ReactTooltip
                      className="tooltip"
                      id="original_travel_date_tooltip"
                      data-cy="voucher-detail-original-travel-tooltip"
                      place="bottom"
                    >
                      <TooltipContainer>
                        <span>Voucher extended</span>
                        {`Original travel end date: ${moment(originalTravelPeriodEnd).format(DATE_DISPLAY_FORMAT)}`}
                      </TooltipContainer>
                    </ReactTooltip>
                  </>
                )}
              </StyledLabel>
              <StyledDateInput
                type="text"
                name="travelPeriodEnd"
                value={moment(travelPeriodEnd).format(DATE_DISPLAY_FORMAT)}
                readOnly
                data-cy="voucher-travel-end"
              />
            </DateItem>
          </DatesWrapper>
        </Column>
        <Column span={4}>
          <StyledLabel htmlFor="language">
            <FormattedMessage id="vouchers.language" />
          </StyledLabel>
          <FormattedMessage id={VoucherLanguages[language]}>
            {(message) => <StyledTextInput name="language" type="text" value={message} readOnly data-cy="voucher-language" />}
          </FormattedMessage>
        </Column>
      </StyledDatesRow>
      <StyledCreatedInfoRow columns={12}>
        <Column span={4}>
          <StyledLabel htmlFor="createdBy">
            <FormattedMessage id="vouchers.createdBy" />
          </StyledLabel>
          <StyledTextInput name="createdBy" type="text" value={createdBy} readOnly data-cy="voucher-created-by" />
        </Column>
        <Column span={4}>
          <StyledLabel htmlFor="createdAt">
            <FormattedMessage id="vouchers.createdAt" />
          </StyledLabel>
          <StyledTextInput
            type="text"
            name="createdAt"
            value={moment(createdAt).format(DATE_TIME_DISPLAY_FORMAT)}
            readOnly
            data-cy="voucher-created-at"
          />
        </Column>
      </StyledCreatedInfoRow>
      {(updatedBy || updatedAt) && (
        <StyledRow columns={12}>
          {updatedBy && (
            <Column span={4}>
              <StyledLabel htmlFor="updatedBy">
                <FormattedMessage id="vouchers.updatedBy" />
              </StyledLabel>
              <StyledTextInput name="updatedBy" type="text" value={updatedBy} readOnly data-cy="voucher-updated-by" />
            </Column>
          )}
          {updatedAt && (
            <Column span={4}>
              <StyledLabel htmlFor="updatedAt">
                <FormattedMessage id="vouchers.updatedAt" />
              </StyledLabel>
              <StyledTextInput
                type="text"
                name="updatedAt"
                value={moment(updatedAt).format(DATE_TIME_DISPLAY_FORMAT)}
                readOnly
                data-cy="voucher-updated-at"
              />
            </Column>
          )}
        </StyledRow>
      )}
      {(activatedAt || inactivatedAt) && (
        <StyledRow columns={12}>
          {activatedAt && (
            <Column span={4}>
              <StyledLabel htmlFor="activatedAt">
                <FormattedMessage id="vouchers.activatedAt" />
              </StyledLabel>
              <StyledTextInput
                type="text"
                name="activatedAt"
                value={moment(activatedAt).format(DATE_TIME_DISPLAY_FORMAT)}
                readOnly
                data-cy="voucher-activated-at"
              />
            </Column>
          )}
          {inactivatedAt && (
            <Column span={4}>
              <StyledLabel htmlFor="inactivatedAt">
                <FormattedMessage id="vouchers.inactivatedAt" />
              </StyledLabel>
              <StyledTextInput
                type="text"
                name="inactivatedAt"
                value={moment(inactivatedAt).format(DATE_TIME_DISPLAY_FORMAT)}
                readOnly
                data-cy="voucher-inactivated-at"
              />
            </Column>
          )}
        </StyledRow>
      )}
      {(associatedVoucher || replacedByVoucher) && (
        <StyledRow columns={12}>
          {associatedVoucher && (
            <Column span={4}>
              <StyledLabel htmlFor="associatedVoucher">
                <FormattedMessage id="vouchers.associatedVoucher" />
              </StyledLabel>
              <StyledLinkableTextInput
                to={`/vouchers/${associatedVoucher}`}
                name="associatedVoucher"
                type="text"
                value={associatedVoucher}
                readOnly
                data-cy="associated-voucher"
              />
            </Column>
          )}
          {replacedByVoucher && (
            <Column span={4}>
              <StyledLabel htmlFor="replacedByVoucher">
                <FormattedMessage id="vouchers.replacedByVoucher" />
              </StyledLabel>
              <StyledLinkableTextInput
                to={`/vouchers/${replacedByVoucher}`}
                name="replacedByVoucher"
                type="text"
                value={replacedByVoucher}
                readOnly
                data-cy="replaced-by-voucher"
              />
            </Column>
          )}
        </StyledRow>
      )}
    </FormContainer>
  );
};

VoucherDetailsTable.propTypes = {
  voucherAlias: PropTypes.string,
  voucherOriginalValue: PropTypes.number,
  voucherCurrentBalance: PropTypes.number,
  voucherCurrency: PropTypes.string,
  voucherDescription: PropTypes.string,
  voucherStatus: PropTypes.string,
  voucherType: PropTypes.string,
  associatedPNR: PropTypes.string,
  salesPeriodStart: PropTypes.string,
  salesPeriodEnd: PropTypes.string,
  travelPeriodStart: PropTypes.string,
  travelPeriodEnd: PropTypes.string,
  createdBy: PropTypes.string,
  createdAt: PropTypes.string,
  updatedBy: PropTypes.string,
  updatedAt: PropTypes.string,
  activatedAt: PropTypes.string,
  inactivatedAt: PropTypes.string,
  language: PropTypes.string,
  associatedVoucher: PropTypes.string,
  replacedByVoucher: PropTypes.string,
  register: PropTypes.func,
  originalSalesPeriodEnd: PropTypes.string,
  originalTravelPeriodEnd: PropTypes.string
};

export default VoucherDetailsTable;
